import { createContext, useContext, useState, useEffect, useRef, forwardRef, type HTMLAttributes } from 'react';
import { type LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';

interface DropdownContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DropdownContext = createContext<DropdownContextValue | undefined>(undefined);

export function DropdownMenu({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <DropdownContext.Provider value={{ open, setOpen }}>
      <div className="relative inline-block text-left">
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

export const DropdownTrigger = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => {
    const context = useContext(DropdownContext);
    if (!context) throw new Error("DropdownTrigger must be used within DropdownMenu");

    return (
      <div
        ref={ref}
        onClick={() => context.setOpen(!context.open)}
        className={cn("cursor-pointer", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DropdownTrigger.displayName = "DropdownTrigger";

export const DropdownContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const context = useContext(DropdownContext);
    const contentRef = useRef<HTMLDivElement>(null);

    if (!context) throw new Error("DropdownContent must be used within DropdownMenu");

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (contentRef.current && !contentRef.current.contains(event.target as Node) && context.open) {
          context.setOpen(false);
        }
      };
      
      // Delay to avoid immediate trigger close
      if (context.open) {
        setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [context.open, context]);

    if (!context.open) return null;

    return (
      <div
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={cn(
          "absolute right-0 mt-2 min-w-[8rem] z-50 overflow-hidden rounded-lg border bg-card p-1 text-card-foreground shadow-lg animate-in fade-in zoom-in-95",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DropdownContent.displayName = "DropdownContent";

export interface DropdownItemProps extends HTMLAttributes<HTMLDivElement> {
  icon?: LucideIcon;
  destructive?: boolean;
}

export const DropdownItem = forwardRef<HTMLDivElement, DropdownItemProps>(
  ({ className, icon: Icon, destructive, onClick, children, ...props }, ref) => {
    const context = useContext(DropdownContext);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (onClick) onClick(e);
      if (context) context.setOpen(false);
    };

    return (
      <div
        ref={ref}
        onClick={handleClick}
        className={cn(
          "relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none transition-colors hover:bg-muted focus:bg-muted",
          destructive && "text-destructive hover:text-destructive hover:bg-destructive/10",
          className
        )}
        {...props}
      >
        {Icon && <Icon className="mr-2 h-4 w-4" />}
        {children}
      </div>
    );
  }
);
DropdownItem.displayName = "DropdownItem";

export const DropdownSeparator = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  )
);
DropdownSeparator.displayName = "DropdownSeparator";
